import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ActivationStatus } from '../../extViewmodels/sdk/models';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { emptyOnForbidden, MIN_SUPPORT_SEARCH_LENGTH, supportQueryRetry } from '../../utils/Support/supportHelpers';
import type { LevStandardQueryMeta } from '../../web/ReactQueryProvider';
import { SUPPORT_PATHS, SUPPORT_QUERY_KEYS } from './supportQueryHelpers';

export interface ISupportRecipientSuggestion {
	userId: string;
	firstName?: string;
	lastName?: string;
	email: string;
	activationStatus?: ActivationStatus;
}

export interface ISupportRecipientSuggestions {
	teammates: ISupportRecipientSuggestion[];
	accountUsers: ISupportRecipientSuggestion[];
}

const EMPTY_SUGGESTIONS: ISupportRecipientSuggestions = { accountUsers: [], teammates: [] };

/** Addresses the composer can offer for a ticket, in two groups. */
export function useSupportRecipientSuggestionsQuery(ticketId: string, query: string, enabled = true) {
	const trimmedQuery = query.trim();
	const userSession = useUserSession();

	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.tickets.recipientSuggestions(ticketId, trimmedQuery),
		// Each debounce boundary is a new key, and without the previous key's rows the open picker would
		// unmount for a round trip — long enough for the agent's Enter to land on nothing.
		placeholderData: keepPreviousData,
		retry: supportQueryRetry,
		queryFn: () =>
			emptyOnForbidden<ISupportRecipientSuggestions>(
				() =>
					userSession.webServiceHelper.callAsync<ISupportRecipientSuggestions>(
						SUPPORT_PATHS.tickets.recipientSuggestions(ticketId, trimmedQuery),
						'GET'
					),
				EMPTY_SUGGESTIONS
			),
		enabled: enabled && !!ticketId && trimmedQuery.length >= MIN_SUPPORT_SEARCH_LENGTH,
		staleTime: 30_000,
		// No toast: an agent mid-address must not be interrupted because a suggestion lookup failed. The
		// empty-string function is this repo's suppress-the-toast-but-keep-the-log form.
		meta: {
			errorLogging: { action: 'RecipientSuggestionsLoad-Failed', category: 'SupportComposer' },
			toastErrorMessage: () => '',
		} satisfies LevStandardQueryMeta,
	});
}
